import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { api } from '../../lib/api'

interface SignerData {
  signer: { token?: string }
  document: { pdfUrl?: string; countryCode: string }
  countryConfig?: { legalBasis?: string }
  auditTrailText?: string
}

interface Props {
  signerData: SignerData
  onComplete: () => void
}

export default function Step1View({ signerData, onComplete }: Props) {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()

  const [scrollPercent, setScrollPercent] = useState(0)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')
  const [pdfLoaded, setPdfLoaded] = useState(false)
  const [manualConfirm, setManualConfirm] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const apiBase = import.meta.env.VITE_API_URL || ''
  const pdfUrl = signerData.document.pdfUrl || `${apiBase}/api/sign/${token}/pdf`

  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight <= clientHeight + 4) { setScrollPercent(100); return }
    setScrollPercent(Math.min(Math.round((scrollTop / (scrollHeight - clientHeight)) * 100), 100))
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => el.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  function handlePdfLoad() {
    setPdfLoaded(true)
    // iframe scroll can't be tracked cross-origin — enable confirm after 2s
    setTimeout(() => setScrollPercent(100), 2000)
  }

  const canConfirm = scrollPercent >= 80 || manualConfirm

  async function handleConfirm() {
    setError('')
    setConfirming(true)
    try {
      await api.post(`/api/sign/${token}/view`)
      onComplete()
    } catch {
      setError(t('sign.view.confirmError', 'Failed to confirm. Please try again.'))
    } finally {
      setConfirming(false)
    }
  }

  const legalText = signerData.countryConfig?.legalBasis || signerData.auditTrailText

  return (
    <div className="space-y-4">
      <div className="bg-white border border-pakt-line p-4 sm:p-6">
        <h2 className="text-xl font-semibold text-pakt-ink mb-1">
          {t('sign.view.title', 'Review the Document')}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('sign.view.instruction', 'Please read the document before confirming.')}
        </p>

        {/* Reading progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between font-pakt-mono text-[10px] tracking-[0.15em] uppercase text-pakt-ink-3 mb-1">
            <span>{t('sign.view.scrollProgress', 'Reading progress')}</span>
            <span>{scrollPercent}%</span>
          </div>
          <div className="w-full h-1.5 bg-pakt-line overflow-hidden">
            <div
              className="h-full bg-pakt-orange transition-all duration-300"
              style={{ width: `${scrollPercent}%` }}
            />
          </div>
        </div>

        {/* Open in new tab — especially useful on iOS */}
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 mb-3 text-sm text-pakt-orange font-medium hover:underline"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M6 2H2v10h10V8M8 2h4v4M8.5 5.5L12 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('sign.view.openPdf', 'Open PDF in new tab')}
        </a>

        {/* PDF iframe */}
        <div
          ref={containerRef}
          className="border border-pakt-line overflow-auto bg-pakt-surface"
          style={{ height: '55vh', minHeight: '280px' }}
        >
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title="Document PDF"
            onLoad={handlePdfLoad}
          />
        </div>

        {/* Manual confirm checkbox — shows after PDF loads (essential for iOS where iframe may not render) */}
        {pdfLoaded && (
          <label className="flex items-start gap-3 mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={manualConfirm}
              onChange={e => setManualConfirm(e.target.checked)}
              className="mt-0.5 border-gray-300 text-pakt-orange focus:ring-pakt-orange"
            />
            <span className="text-sm text-gray-700">
              {t('sign.view.manualConfirm', 'I confirm that I have read and understood the document.')}
            </span>
          </label>
        )}
      </div>

      {/* Legal disclaimer */}
      <div className="bg-pakt-surface border border-pakt-line p-4">
        <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-ink-3 uppercase mb-1">
          {t('sign.view.legalTitle', 'Legal Disclaimer')}
        </div>
        <p className="text-xs text-gray-600">
          {t('sign.view.legalText', 'By confirming, you acknowledge that you have read and understood the document. Electronic signatures are legally binding under applicable law.')}
        </p>
        {legalText && (
          <p className="text-xs text-pakt-ink-2 mt-1 font-pakt-mono">{legalText}</p>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <button
        onClick={handleConfirm}
        disabled={!canConfirm || confirming}
        className={`w-full py-4 font-semibold transition text-base ${
          canConfirm
            ? 'bg-pakt-orange text-white hover:bg-pakt-orange-dk'
            : 'bg-pakt-line text-pakt-ink-3 cursor-not-allowed'
        } disabled:opacity-50`}
      >
        {confirming
          ? t('sign.view.confirming', 'Confirming...')
          : canConfirm
            ? t('sign.view.confirm', 'Confirm Reading')
            : t('sign.view.scrollMore', 'Read document to continue')}
      </button>
    </div>
  )
}
